
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { UserProfile, UserSettings, RoutineProgress, ClassSubscription } from './types';

export const useUserProfile = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      return data as UserProfile | null;
    },
    enabled: !!user,
  });
};

export const useUserSettings = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-settings', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      return data as UserSettings | null;
    },
    enabled: !!user,
  });
};

export const useRoutineProgress = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['routine-progress', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data } = await supabase
        .from('routine_progress')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      return data as RoutineProgress[] || [];
    },
    enabled: !!user,
  });
};

export const useClassSubscriptions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['class-subscriptions', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data } = await supabase
        .from('class_subscriptions')
        .select('*')
        .eq('user_id', user.id);

      return data as ClassSubscription[] || [];
    },
    enabled: !!user,
  });
};
